import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TransactionDetailsModal = ({ isOpen, onClose, transaction }) => {
  if (!isOpen || !transaction) return null;

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-success/10 text-success border-success/20',
      pending: 'bg-warning/10 text-warning border-warning/20',
      in_review: 'bg-info/10 text-info border-info/20',
      failed: 'bg-error/10 text-error border-error/20'
    };
    return styles?.[status] || styles?.pending;
  };

  const handleDownloadReceipt = () => {
    // Generate PDF receipt
    const receiptContent = `
      TRANSACTION RECEIPT
      ==================
      
      Transaction ID: ${transaction?.id}
      Date: ${new Date(transaction?.date)?.toLocaleString()}
      Status: ${transaction?.status?.toUpperCase()}
      
      FROM
      ====
      Customer: ${transaction?.customer}
      Amount Sent: ${transaction?.sendAmount} ${transaction?.fromCurrency}
      
      TO
      ==
      Recipient: ${transaction?.recipient}
      Country: ${transaction?.recipientCountry}
      Amount Received: ${transaction?.receiveAmount} ${transaction?.toCurrency}
      
      DETAILS
      =======
      Exchange Rate: 1 ${transaction?.fromCurrency} = ${transaction?.exchangeRate} ${transaction?.toCurrency}
      Transfer Fee: ${transaction?.fee} ${transaction?.fromCurrency}
      Payment Method: ${transaction?.paymentMethod}
      Reference: ${transaction?.reference}
      Commission: $${transaction?.commission}
    `;

    const blob = new Blob([receiptContent], { type: 'text/plain' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `receipt-${transaction?.id}.txt`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    window.URL?.revokeObjectURL(url);
  };

  const handlePrintReceipt = () => {
    const printWindow = window.open('', '_blank');
    printWindow?.document?.write(`
      <html>
        <head>
          <title>Transaction Receipt - ${transaction?.id}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; }
            h1 { font-size: 24px; margin-bottom: 20px; }
            .section { margin-bottom: 20px; }
            .section-title { font-weight: bold; margin-bottom: 10px; }
            .row { display: flex; justify-content: space-between; margin: 5px 0; }
            .label { color: #666; }
            .value { font-weight: bold; }
          </style>
        </head>
        <body>
          <h1>Transaction Receipt</h1>
          <div class="section">
            <div class="row">
              <span class="label">Transaction ID:</span>
              <span class="value">${transaction?.id}</span>
            </div>
            <div class="row">
              <span class="label">Date:</span>
              <span class="value">${new Date(transaction?.date)?.toLocaleString()}</span>
            </div>
            <div class="row">
              <span class="label">Status:</span>
              <span class="value">${transaction?.status?.toUpperCase()}</span>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Sender Information</div>
            <div class="row">
              <span class="label">Customer:</span>
              <span class="value">${transaction?.customer}</span>
            </div>
            <div class="row">
              <span class="label">Amount Sent:</span>
              <span class="value">${transaction?.sendAmount} ${transaction?.fromCurrency}</span>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Recipient Information</div>
            <div class="row">
              <span class="label">Recipient:</span>
              <span class="value">${transaction?.recipient}</span>
            </div>
            <div class="row">
              <span class="label">Country:</span>
              <span class="value">${transaction?.recipientCountry}</span>
            </div>
            <div class="row">
              <span class="label">Amount Received:</span>
              <span class="value">${transaction?.receiveAmount} ${transaction?.toCurrency}</span>
            </div>
          </div>
          <div class="section">
            <div class="section-title">Transaction Details</div>
            <div class="row">
              <span class="label">Exchange Rate:</span>
              <span class="value">1 ${transaction?.fromCurrency} = ${transaction?.exchangeRate} ${transaction?.toCurrency}</span>
            </div>
            <div class="row">
              <span class="label">Transfer Fee:</span>
              <span class="value">${transaction?.fee} ${transaction?.fromCurrency}</span>
            </div>
            <div class="row">
              <span class="label">Payment Method:</span>
              <span class="value">${transaction?.paymentMethod}</span>
            </div>
            <div class="row">
              <span class="label">Reference:</span>
              <span class="value">${transaction?.reference}</span>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 100);
            };
          </script>
        </body>
      </html>
    `);
    printWindow?.document?.close();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-auto">
        <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-background">
          <h2 className="text-xl font-semibold text-foreground">Transaction Details</h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <h3 className="text-2xl font-semibold text-foreground">
                Transaction Information
              </h3>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">ID: {transaction?.id}</span>
                <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(transaction?.status)}`}>
                  {transaction?.status?.toUpperCase()}
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-sm text-muted-foreground">Date & Time</div>
              <div className="text-sm font-medium text-foreground">
                {new Date(transaction?.date)?.toLocaleString()}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">Sender Information</h4>
              <div className="space-y-3 bg-muted/50 rounded-lg p-4">
                <div>
                  <div className="text-sm text-muted-foreground">Customer</div>
                  <div className="text-sm font-medium text-foreground">{transaction?.customer}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Customer ID</div>
                  <div className="text-sm font-medium text-foreground">{transaction?.customerId}</div>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-semibold text-foreground">Recipient Information</h4>
              <div className="space-y-3 bg-muted/50 rounded-lg p-4">
                <div>
                  <div className="text-sm text-muted-foreground">Recipient</div>
                  <div className="text-sm font-medium text-foreground">{transaction?.recipient}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Country</div>
                  <div className="text-sm font-medium text-foreground">{transaction?.recipientCountry}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Payment Method</div>
                  <div className="text-sm font-medium text-foreground">{transaction?.paymentMethod}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 bg-muted/50 rounded-lg p-4">
            <h4 className="font-semibold text-foreground">Transaction Details</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Send Amount</span>
                  <span className="text-sm font-medium text-foreground">
                    {transaction?.sendAmount} {transaction?.fromCurrency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Exchange Rate</span>
                  <span className="text-sm font-medium text-foreground">
                    1 {transaction?.fromCurrency} = {transaction?.exchangeRate} {transaction?.toCurrency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Transfer Fee</span>
                  <span className="text-sm font-medium text-foreground">
                    {transaction?.fee} {transaction?.fromCurrency}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Receive Amount</span>
                  <span className="text-sm font-medium text-success">
                    {transaction?.receiveAmount} {transaction?.toCurrency}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Your Commission</span>
                  <span className="text-sm font-medium text-success">
                    ${transaction?.commission}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Reference</span>
                  <span className="text-sm font-medium text-foreground">
                    {transaction?.reference}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {transaction?.failureReason && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-error mb-2">
                <Icon name="AlertCircle" size={16} />
                <span className="font-medium">Transaction Failed</span>
              </div>
              <p className="text-sm text-error/80">{transaction?.failureReason}</p>
            </div>
          )}

          {transaction?.reviewReason && (
            <div className="bg-info/10 border border-info/20 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-info mb-2">
                <Icon name="AlertCircle" size={16} />
                <span className="font-medium">Under Review</span>
              </div>
              <p className="text-sm text-info/80">{transaction?.reviewReason}</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border flex justify-end space-x-3">
          <Button variant="outline" onClick={handleDownloadReceipt}>
            <Icon name="Download" size={16} className="mr-2" />
            Download Receipt
          </Button>
          <Button variant="outline" onClick={handlePrintReceipt}>
            <Icon name="Printer" size={16} className="mr-2" />
            Print Receipt
          </Button>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

export default TransactionDetailsModal;